import React, { useEffect, useState } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { messages } from "../i18n-messages";
import { AutoSearch, Loader, LoaderTypes } from "~/core";
import { FileImportAPI, ISelectOption } from "@ai360/core";
import { getInitialEventCount } from "~/customer-data/selectors";
import { useAppSelector } from "~/store/hooks";

interface EventIdSearchProps {
    forceUpdate: () => void;
    intl: intlShape;
    nonFieldFeaturesLoading: boolean;
    nonFieldFeatureOptions: ISelectOption<string>[];
    getNonFieldFeatureOptions: () => void;
    onSelection: (selection) => void;
    userGuid: string;
}

interface AutoSearchOption {
    value: string;
    label: string;
    secondaryPropList?: string;
    nonFieldFeatureGuid?: string;
}

const EVENT_ID_THRESHOLD = 200000;

export const EventIdSearch = (props: EventIdSearchProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [eventIdOptions, setEventIdOptions] = useState<AutoSearchOption[]>([]);
    const [resetOptions, setResetOptions] = useState<boolean>(false);
    const eventCount = useAppSelector(getInitialEventCount);
    const preload = eventCount && eventCount < EVENT_ID_THRESHOLD;

    useEffect(() => {
        if (preload && !isLoading) {
            getValidEventIds(props.userGuid, "");
        }
        if (!props.nonFieldFeatureOptions || props.nonFieldFeatureOptions.length === 0) {
            props.getNonFieldFeatureOptions();
        }
    }, []);

    const getAutoSearchList = (
        userGuid: string,
        searchString: string
    ): Promise<AutoSearchOption[]> => {
        if (!searchString || !userGuid) {
            return Promise.resolve(eventIdOptions);
        }
        const meetsSearchThreshold = [...searchString]?.length >= 2;
        if (!meetsSearchThreshold) {
            return Promise.resolve(eventIdOptions);
        }

        setEventIdOptions([]);
        return getValidEventIds(userGuid, searchString);
    };

    const getValidEventIds = (userGuid, searchString: string): Promise<AutoSearchOption[]> => {
        setIsLoading(true);
        return FileImportAPI.getValidEventIds(userGuid, searchString, "", "", "", [], true)
            .then((results: FileImportAPI.IEventIdOption[]) => {
                const mapped = (results || []).map((events) => ({
                    value: events.fieldGuid ?? events.nonFieldFeatureGuid,
                    label: events.eventId,
                    secondaryPropList:
                        events.customerFarmFieldLabel ?? events.customerNonFieldFeatureLabel,
                    nonFieldFeatureGuid: events.nonFieldFeatureGuid,
                }));
                const sortedOptions = mapped.sort((a, b) => a.label.localeCompare(b.label));
                setEventIdOptions(sortedOptions);
                setResetOptions(true);
                return sortedOptions;
            })
            .catch(() => {
                setEventIdOptions([]);
                return [];
            })
            .finally(() => {
                setIsLoading(false);
                props.forceUpdate();
            });
    };

    const onResetOptions = (): void => {
        setResetOptions(false);
    };

    const onSelection = (selection: Record<string, any>): void => {
        props.onSelection(selection);
    };

    return (
        <div className="auto-search-pane-content event-search-pane-content">
            {(isLoading || props.nonFieldFeaturesLoading) && (
                <Loader className="event-id-loader" type={LoaderTypes.BALL_CLIP_ROTATE} />
            )}
            <AutoSearch
                getAutoSearchList={preload ? null : getAutoSearchList}
                initialFilterStr=""
                itemList={preload ? eventIdOptions : []}
                keyProp={"value"}
                nameProp={"label"}
                onResetOptions={onResetOptions}
                onSearchChange={() => {
                    if (!preload) setEventIdOptions([]);
                }}
                onSelection={(s) => onSelection(s)}
                placeholderText={
                    preload
                        ? props.intl.formatMessage(messages.eventIdSearchLbl)
                        : props.intl.formatMessage(messages.eventIdSearchLblAlt)
                }
                resetOptions={resetOptions}
                secondaryPropList={["secondaryPropList"]}
                userGuid={props.userGuid}
            />
        </div>
    );
};

export default injectIntl(EventIdSearch);
